import { inject, Injectable } from '@angular/core';
import {
    LocalStorageNotificationUpdateService,
    NOTIFICATION_STATUS_CODE,
    NotificationDataInstanceToken,
    NotificationDataInterface,
    NotificationModel,
} from '@evorait/pace-core';
import { BadgeInfo, deriveStatusBadge, STATUS_BADGE } from './status-badge.model';

// Re-exported for the existing consumers that import them from this file; the badge matrix
// itself lives in the model so it can be applied to store state without this service.
export { BadgeInfo, STATUS_BADGE };

@Injectable({
    providedIn: 'root',
})
export class StatusBadgeService {
    private readonly localStorageNotificationUpdateService = inject(LocalStorageNotificationUpdateService);
    private readonly notificationService = inject<NotificationDataInterface>(NotificationDataInstanceToken);

    /**
     * Returns the translations key part based on the statusCode that is passed into it and based on the timeStamp.
     * If timeStamp indicates an overdue notification this is most likely returned. Otherwise its tried to find a
     * respective value based on the badge matrix.
     * It first tries to use statusCode and timestamp information from a locally stored statusCode. If the is not existing
     * it uses the information sent by the backend. This implies that an already accepted notification with ETA, which
     * was set to a different time than PlanStartTime, that this information is lost if the local storage information
     * is lost (due to new installation).
     *
     * @param notification the notification object
     * @returns the status badge object, or `undefined` for a status that carries no badge (CRM)
     */
    public async getBadgeStatusAsync(notification: NotificationModel): Promise<BadgeInfo | undefined> {
        // CANC overwrites everything
        return this.localStorageNotificationUpdateService
            .getLatestNewUpdateAsync(notification.identifier)
            .then((newUpdate) => {
                const status = this.notificationService.getStatus(notification);
                const statusCode =
                    newUpdate && newUpdate.messageType === NOTIFICATION_STATUS_CODE.CANC
                        ? newUpdate.messageType
                        : status.statusCode;

                return deriveStatusBadge({
                    statusCode,
                    isCancelled: notification.isCancelled,
                    start: this.notificationService.getNotificationStart(notification),
                });
            });
    }
}
